import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, User, MapPin, Building, CreditCard, BarChart3, Eye, Loader2, Percent, GitMerge, Link2, Printer } from 'lucide-react'
import SubmissaoCadastroPublicoCard from '../../components/clientes/SubmissaoCadastroPublicoCard'
import ImprimirClienteModal from '../../components/clientes/ImprimirClienteModal'
import clientesService from '../../services/clientesService'
import { pisteirosService } from '../../services/pisteirosService'
import { formatCpfCnpj, formatPhone, formatCep } from '../../utils/masks'
import { isValidCpfCnpj, fetchCEP, fetchCNPJ } from '../../utils/validators'
import { useToast } from '../../hooks/useToast'
import Toast from '../../components/ui/Toast'
import CidadeAutocomplete from '../../components/ui/CidadeAutocomplete'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { usePermissao } from '../../hooks/usePermissao'
import { useAuthStore } from '../../stores/authStore'
import FazendasTab from '../../components/clientes/FazendasTab'
import DadosBancariosTab from '../../components/clientes/DadosBancariosTab'
import BalancoTab from '../../components/clientes/BalancoTab'
import MesclarClienteModal from '../../components/clientes/MesclarClienteModal'
import toast from 'react-hot-toast'

export default function ClienteDetalhes() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toasts, success, error, hideToast } = useToast()
  const { organizadorAtivo } = useOrganizador()
  const [cliente, setCliente] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('dados')
  const [formData, setFormData] = useState({
    nome: '',
    apelido: '',
    cpf_cnpj: '',
    rg_ie: '',
    inscricao_estadual: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    telefone: '',
    telefone2: '',
    email: '',
    grupo: '',
    observacoes: '',
    is_socio: false,
    restrito_compra: false,
    restrito_compra_motivo: '',
    pisteiro_id: '',
    classificacao_manual: '',
    comissao_venda_pct: '',
    comissao_compra_pct: '',
    comissao_defesa_pct: ''
  })
  const [cpfCnpjError, setCpfCnpjError] = useState('')
  const [cnpjLoading, setCnpjLoading] = useState(false)
  const [cnpjStatus, setCnpjStatus] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [cpfSuggestions, setCpfSuggestions] = useState([])
  const [showCpfSuggestions, setShowCpfSuggestions] = useState(false)
  const [pisteiros, setPisteiros] = useState([])
  const [submissoesCadastro, setSubmissoesCadastro] = useState([])
  const [loadingSubmissoes, setLoadingSubmissoes] = useState(false)

  const { soVisualizacao, podeEditar } = usePermissao()
  const { user } = useAuthStore()
  const isAdmin = user?.permission_level === 'admin' || user?.permission_level === 'super_admin'
  // Mesclar duplicado: admin ou personalizado com acesso completo a clientes
  const podeMesclar = isAdmin || (user?.permission_level === 'personalizado' && podeEditar('clientes'))
  const [showMesclarModal, setShowMesclarModal] = useState(false)
  const [showImprimirModal, setShowImprimirModal] = useState(false)
  const organizadorId = organizadorAtivo?.id
  const isNew = id === 'novo'
  const readOnly = !isNew && soVisualizacao('clientes')

  useEffect(() => {
    if (!organizadorId) {
      setLoading(false)
      return
    }
    
    if (!isNew) {
      loadCliente()
    } else {
      setLoading(false)
    }
  }, [id, organizadorId])

  useEffect(() => {
    if (organizadorId && organizadorAtivo?.produtos_premium?.includes('quem_vendeu_trouxe')) {
      pisteirosService.listar(organizadorId)
        .then(res => setPisteiros(res.data || []))
        .catch(() => setPisteiros([]))
    }
  }, [organizadorId, organizadorAtivo])

  useEffect(() => {
    if (organizadorId && cliente?.id && cliente?.origem_cadastro === 'link_publico') {
      setLoadingSubmissoes(true)
      clientesService.getSubmissoesCadastroPublico(organizadorId, cliente.id)
        .then(setSubmissoesCadastro)
        .catch(() => setSubmissoesCadastro([]))
        .finally(() => setLoadingSubmissoes(false))
    }
  }, [organizadorId, cliente?.id, cliente?.origem_cadastro])



  const loadCliente = async () => {
    if (!organizadorId) return
    
    try {
      setLoading(true)
      const data = await clientesService.getById(organizadorId, id)
      setCliente(data)
      setFormData({
        nome: data.nome || '',
        apelido: data.apelido || '',
        cpf_cnpj: data.cpf_cnpj || '',
        rg_ie: data.rg_ie || '',
        inscricao_estadual: data.inscricao_estadual || '',
        endereco: data.endereco || '',
        numero: data.numero || '',
        complemento: data.complemento || '',
        bairro: data.bairro || '',
        cidade: data.cidade || '',
        estado: data.estado || '',
        cep: data.cep || '',
        telefone: data.telefone || '',
        telefone2: data.telefone2 || '',
        email: data.email || '',
        grupo: data.grupo || '',
        observacoes: data.observacoes || '',
        is_socio: data.is_socio || false,
        restrito_compra: data.restrito_compra || false,
        restrito_compra_motivo: data.restrito_compra_motivo || '',
        pisteiro_id: data.pisteiro_id ? String(data.pisteiro_id) : '',
        classificacao_manual: data.classificacao_manual || '',
        comissao_venda_pct:  data.comissao_venda_pct  != null ? String(data.comissao_venda_pct)  : '',
        comissao_compra_pct: data.comissao_compra_pct != null ? String(data.comissao_compra_pct) : '',
        comissao_defesa_pct: data.comissao_defesa_pct != null ? String(data.comissao_defesa_pct) : ''
      })
    } catch (err) {
      console.error('Erro ao carregar cliente:', err)
      error('Erro ao carregar cliente')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validar se organizador está selecionado
    if (!organizadorId) {
      error('Nenhum organizador selecionado')
      return
    }
    
    // Validar CPF/CNPJ se preenchido
    if (formData.cpf_cnpj && !isValidCpfCnpj(formData.cpf_cnpj)) {
      setCpfCnpjError('CPF/CNPJ inválido')
      return
    }
    setCpfCnpjError('')

    // Validar motivo da restrição se marcado como restrito
    if (formData.restrito_compra && !formData.restrito_compra_motivo.trim()) {
      error('Informe o motivo da restrição de compra')
      return
    }

    const parsePct = (v) => (v === '' || v == null) ? null : parseFloat(v)
    const payload = {
      ...formData,
      comissao_venda_pct:  parsePct(formData.comissao_venda_pct),
      comissao_compra_pct: parsePct(formData.comissao_compra_pct),
      comissao_defesa_pct: parsePct(formData.comissao_defesa_pct),
      restrito_compra_motivo: formData.restrito_compra ? formData.restrito_compra_motivo : null,
      pisteiro_id: formData.pisteiro_id ? parseInt(formData.pisteiro_id) : null,
      classificacao_manual: formData.classificacao_manual || null
    }

    try {
      if (isNew) {
        const novoCliente = await clientesService.create(organizadorId, payload)
        success('Cliente criado com sucesso!')
        navigate(`/clientes/${novoCliente.id}`, { replace: true })
      } else {
        await clientesService.update(organizadorId, id, payload)
        success('Cliente atualizado com sucesso!')
        await loadCliente()
      }
    } catch (err) {
      console.error('Erro ao salvar cliente:', err)
      const errorMessage = err.response?.data?.detail || 'Erro ao salvar cliente'
      error(errorMessage)
    }
  }

  const handleCpfCnpjChange = async (value) => {
    const formatted = formatCpfCnpj(value)
    setFormData({ ...formData, cpf_cnpj: formatted })
    setCpfCnpjError('')

    const digits = formatted.replace(/[^\d]/g, '')

    // Alerta de possível duplicata enquanto digita (mesma ideia da busca por nome)
    if (isNew && digits.length >= 4) {
      try {
        const results = await clientesService.searchByCpfCnpj(organizadorId, formatted)
        setCpfSuggestions(results)
        setShowCpfSuggestions(results.length > 0)
      } catch (err) {
        console.error('Erro ao buscar CPF/CNPJ:', err)
      }
    } else {
      setCpfSuggestions([])
      setShowCpfSuggestions(false)
    }

    // Auto-fill only for CNPJ (14 digits)
    if (digits.length === 14) {
      setCnpjLoading(true)
      setCnpjStatus('')
      try {
        const data = await fetchCNPJ(formatted)
        if (data) {
          setCnpjStatus(data.situacao || '')
          setFormData(prev => ({
            ...prev,
            cpf_cnpj: formatted,
            nome: prev.nome || data.nome || '',
            email: prev.email || data.email || '',
            telefone: prev.telefone || data.telefone || '',
            endereco: data.endereco || prev.endereco,
            numero: data.numero || prev.numero,
            complemento: data.complemento || prev.complemento,
            bairro: data.bairro || prev.bairro,
            cidade: data.cidade || prev.cidade,
            estado: data.estado || prev.estado,
            cep: data.cep ? formatCep(data.cep) : prev.cep,
          }))
        }
      } finally {
        setCnpjLoading(false)
      }
    } else {
      setCnpjStatus('')
    }
  }

  const handleCepChange = async (cep) => {
    const formattedCep = formatCep(cep)
    setFormData({...formData, cep: formattedCep})

    if (formattedCep.replace(/[^\d]/g, '').length === 8) {
      const cepData = await fetchCEP(formattedCep)
      if (cepData) {
        setFormData(prev => ({
          ...prev,
          endereco: cepData.endereco || prev.endereco,
          bairro: cepData.bairro || prev.bairro,
          cidade: cepData.cidade || prev.cidade,
          estado: cepData.estado || prev.estado
        }))
      }
    }
  }

  const handleNameChange = async (value) => {
    setFormData({...formData, nome: value})
    
    if (value.length >= 2 && isNew) {
      try {
        const results = await clientesService.searchByName(organizadorId, value)
        setSuggestions(results)
        setShowSuggestions(results.length > 0)
      } catch (err) {
        console.error('Erro ao buscar nomes:', err)
      }
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rematei-blue"></div>
      </div>
    )
  }

  // Abas base
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'
  const baseTabs = [
    { id: 'dados',     label: 'Dados Principais', icon: User },
    { id: 'endereco',  label: 'Endereço',          icon: MapPin },
    { id: 'fazendas',  label: isFluxoCavalo ? 'Haras' : 'Fazendas', icon: Building },
    { id: 'bancarios', label: 'Dados Bancários',   icon: CreditCard },
    { id: 'comissoes', label: 'Comissões',         icon: Percent },
    { id: 'balanco',   label: 'Balanço',           icon: BarChart3 },
  ]

  // Abas condicionais baseadas em produtos premium
  const conditionalTabs = []
  
  // Aba Análise 360° - só se organizador tem o produto premium
  if (organizadorAtivo?.produtos_premium?.includes('analise_360')) {
    conditionalTabs.push({ id: 'analise', label: 'Análise 360°', icon: Eye })
  }

  // Aba de histórico do link de cadastro público — só aparece pra quem veio por lá
  if (cliente?.origem_cadastro === 'link_publico') {
    conditionalTabs.push({ id: 'cadastro-publico', label: 'Cadastro Público', icon: Link2 })
  }

  const tabs = [...baseTabs, ...conditionalTabs]

  return (
    <>
      {/* Modal de mesclagem */}
      {podeMesclar && !isNew && (
        <MesclarClienteModal
          isOpen={showMesclarModal}
          onClose={() => setShowMesclarModal(false)}
          cliente={cliente}
          organizadorId={organizadorId}
          onSuccess={(destinoId) => {
            success('Clientes mesclados com sucesso!')
            navigate(`/clientes/${destinoId}`)
          }}
        />
      )}

      {/* Modal de impressão */}
      {showImprimirModal && !isNew && (
        <ImprimirClienteModal
          cliente={cliente}
          organizadorId={organizadorId}
          labelFazendas={isFluxoCavalo ? 'Haras' : 'Fazendas'}
          temCadastroPublico={cliente?.origem_cadastro === 'link_publico'}
          onClose={() => setShowImprimirModal(false)}
        />
      )}

      {/* Toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}

      <div className="page-content">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/clientes')}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/20 transition-all"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                {isNew ? 'Novo Cliente' : (cliente?.nome || '—')}
                {!isNew && cliente?.origem_pisteiro_remoto && (
                  <span className="text-xs font-semibold bg-white/20 text-white px-2 py-0.5 rounded-full whitespace-nowrap" title="Comprador informado por um pisteiro remoto (Painel V2)">
                    🚀 Cliente Rematei
                  </span>
                )}
                {!isNew && cliente?.origem_cadastro === 'link_publico' && (
                  <span className="flex items-center gap-1 text-xs font-semibold bg-white/20 text-white px-2 py-0.5 rounded-full whitespace-nowrap" title="Cadastrado através do link de cadastro público">
                    <Link2 className="w-3 h-3" /> Via Link de Cadastro
                  </span>
                )}
              </h1>
              {!isNew && cliente?.cidade && (
                <p className="text-blue-200 text-sm mt-0.5">{cliente.cidade}/{cliente.estado}</p>
              )}
            </div>
          </div>
          {!isNew && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowImprimirModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white transition-all text-sm font-medium"
                title="Imprimir cadastro do cliente"
              >
                <Printer className="w-4 h-4" />
                <span className="hidden sm:inline">Imprimir</span>
              </button>
              {podeMesclar && (
                <button
                  onClick={() => setShowMesclarModal(true)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/15 hover:bg-white/25 text-white transition-all text-sm font-medium"
                  title="Mesclar com outro cliente duplicado"
                >
                  <GitMerge className="w-4 h-4" />
                  <span className="hidden sm:inline">Mesclar duplicado</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-rematei-blue text-rematei-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>

        {/* Content */}
        {['dados', 'endereco', 'comissoes', 'analise'].includes(activeTab) && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {readOnly && (
              <div className="px-6 pt-4 pb-0">
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <span className="font-medium">Modo somente leitura</span>
                  <span className="text-amber-600">— você não tem permissão para editar este cliente.</span>
                </div>
              </div>
            )}
            <form onSubmit={readOnly ? (e) => e.preventDefault() : handleSubmit} className="p-6">
            {/* Dados Principais */}
            {activeTab === 'dados' && (
              <div className="space-y-6">
                {/* Classificação do Cadastro */}
                {!isNew && (() => {
                  // Calcula classificação automática
                  const temNome = !!formData.nome
                  const temCpf = !!formData.cpf_cnpj
                  const temTelefone = !!formData.telefone
                  const temCidade = !!formData.cidade
                  const temEndereco = !!formData.endereco && !!formData.bairro && !!formData.cep
                  const temFazenda = cliente?.fazendas?.length > 0
                  const temBanco = cliente?.dados_bancarios?.length > 0

                  const pontos = [temNome, temCpf, temTelefone, temCidade, temEndereco, temFazenda, temBanco].filter(Boolean).length
                  const total = 7
                  const pct = Math.round((pontos / total) * 100)

                  let classificacaoAuto = 'bronze'
                  if (temNome && temCpf && temTelefone && temCidade) classificacaoAuto = 'prata'
                  if (classificacaoAuto === 'prata' && temEndereco && temFazenda && temBanco) classificacaoAuto = 'ouro'

                  const classificacao = formData.classificacao_manual || classificacaoAuto

                  const cores = {
                    bronze: { bg: 'bg-amber-100', text: 'text-amber-700', bar: 'bg-amber-500', icon: '🥉' },
                    prata: { bg: 'bg-gray-100', text: 'text-gray-600', bar: 'bg-gray-400', icon: '🥈' },
                    ouro: { bg: 'bg-yellow-100', text: 'text-yellow-700', bar: 'bg-yellow-500', icon: '🥇' },
                  }
                  const cor = cores[classificacao] || cores.bronze

                  const faltam = []
                  if (!temCpf) faltam.push('CPF/CNPJ')
                  if (!temTelefone) faltam.push('Telefone')
                  if (!temCidade) faltam.push('Cidade')
                  if (!temEndereco) faltam.push('Endereço completo')
                  if (!temFazenda) faltam.push('Fazenda')
                  if (!temBanco) faltam.push('Dados bancários')

                  return (
                    <div className={`rounded-xl border p-4 ${cor.bg} border-opacity-50`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{cor.icon}</span>
                          <span className={`text-sm font-bold uppercase ${cor.text}`}>
                            {classificacao}
                          </span>
                          {formData.classificacao_manual && (
                            <span className="text-xs text-gray-400">(manual)</span>
                          )}
                          {!formData.classificacao_manual && (
                            <span className="text-xs text-gray-400">(automático)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{pontos}/{total} campos</span>
                          <select
                            value={formData.classificacao_manual}
                            onChange={(e) => !readOnly && setFormData({...formData, classificacao_manual: e.target.value})}
                            className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-white"
                            disabled={readOnly}
                          >
                            <option value="">Automático</option>
                            <option value="bronze">🥉 Bronze</option>
                            <option value="prata">🥈 Prata</option>
                            <option value="ouro">🥇 Ouro</option>
                          </select>
                        </div>
                      </div>
                      {/* Barra de progresso */}
                      <div className="w-full bg-white/60 rounded-full h-2 mb-2">
                        <div className={`h-2 rounded-full transition-all ${cor.bar}`} style={{ width: `${pct}%` }} />
                      </div>
                      {faltam.length > 0 && (
                        <p className="text-xs text-gray-500">
                          Falta preencher: {faltam.join(', ')}
                        </p>
                      )}
                    </div>
                  )
                })()}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.nome}
                      onChange={(e) => !readOnly && handleNameChange(e.target.value)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      className={`input-rematei ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                      readOnly={readOnly}
                      placeholder="Nome completo do cliente"
                    />

                    {/* Sugestões de nomes */}
                    {showSuggestions && suggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 text-xs text-gray-500 bg-yellow-50 border-b">
                          ⚠️ Clientes similares encontrados - evite duplicatas
                        </div>
                        {suggestions.map((suggestion) => (
                          <div
                            key={suggestion.id}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onClick={() => {
                              setShowSuggestions(false)
                              // Não preenche automaticamente, apenas alerta
                            }}
                          >
                            <div className="font-medium text-gray-900">{suggestion.nome}</div>
                            {suggestion.cpf_cnpj && (
                              <div className="text-sm text-gray-500">{suggestion.cpf_cnpj}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Apelido
                    </label>
                    <input
                      type="text"
                      value={formData.apelido}
                      onChange={(e) => !readOnly && setFormData({ ...formData, apelido: e.target.value })}
                      className={`input-rematei ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                      readOnly={readOnly}
                      placeholder="Apelido (opcional)"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CPF/CNPJ
                      {cnpjLoading && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs text-blue-500 font-normal">
                          <Loader2 className="w-3 h-3 animate-spin" /> Buscando dados...
                        </span>
                      )}
                      {cnpjStatus && !cnpjLoading && (
                        <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          cnpjStatus === 'ATIVA'
                            ? 'bg-emerald-100 text-emerald-700'
                            : cnpjStatus === 'BAIXADA' || cnpjStatus === 'INAPTA'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {cnpjStatus}
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={formData.cpf_cnpj}
                      onChange={(e) => handleCpfCnpjChange(e.target.value)}
                      onBlur={() => setTimeout(() => setShowCpfSuggestions(false), 200)}
                      className={`input-rematei ${cpfCnpjError ? 'border-red-500' : ''}`}
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      maxLength="18"
                    />
                    {cpfCnpjError && (
                      <p className="text-red-500 text-sm mt-1">{cpfCnpjError}</p>
                    )}

                    {/* Sugestões de CPF/CNPJ parecidos */}
                    {showCpfSuggestions && cpfSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 text-xs text-gray-500 bg-yellow-50 border-b">
                          ⚠️ Clientes com CPF/CNPJ parecido - evite duplicatas
                        </div>
                        {cpfSuggestions.map((suggestion) => (
                          <div
                            key={suggestion.id}
                            className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onClick={() => setShowCpfSuggestions(false)}
                          >
                            <div className="font-medium text-gray-900">{suggestion.nome}</div>
                            <div className="text-sm text-gray-500">
                              {suggestion.cpf_cnpj}
                              {suggestion.cidade && ` · ${suggestion.cidade}/${suggestion.estado}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      RG/IE
                    </label>
                    <input
                      type="text"
                      value={formData.rg_ie}
                      onChange={(e) => setFormData({...formData, rg_ie: e.target.value})}
                      className="input-rematei"
                      placeholder="RG ou Inscrição Estadual"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone Principal
                    </label>
                    <input
                      type="text"
                      value={formData.telefone}
                      onChange={(e) => {
                        const formatted = formatPhone(e.target.value)
                        setFormData({...formData, telefone: formatted})
                      }}
                      className="input-rematei"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone 2
                    </label>
                    <input
                      type="text"
                      value={formData.telefone2}
                      onChange={(e) => {
                        const formatted = formatPhone(e.target.value)
                        setFormData({...formData, telefone2: formatted})
                      }}
                      className="input-rematei"
                      placeholder="(00) 00000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="input-rematei"
                      placeholder="cliente@exemplo.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Grupo
                    </label>
                    <input
                      type="text"
                      list="grupos-list"
                      value={formData.grupo}
                      onChange={(e) => setFormData({...formData, grupo: e.target.value})}
                      className="input-rematei"
                      placeholder="Selecione ou digite um grupo..."
                    />
                    <datalist id="grupos-list">
                      <option value="Comprador" />
                      <option value="Vendedor" />
                      <option value="Consignador" />
                      <option value="Parceiro" />
                    </datalist>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_socio"
                      checked={formData.is_socio}
                      onChange={(e) => {
                        const novoValor = e.target.checked
                        const msg = novoValor
                          ? 'Marcar este cliente como sócio?'
                          : 'Desmarcar como sócio? A sociedade ativa será cancelada.'
                        toast((t) => (
                          <div className="flex flex-col gap-2">
                            <span className="text-sm font-medium text-gray-800">{msg}</span>
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => { toast.dismiss(t.id) }}
                                className="px-3 py-1 text-xs border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50"
                              >
                                Cancelar
                              </button>
                              <button
                                onClick={() => {
                                  setFormData(prev => ({...prev, is_socio: novoValor}))
                                  toast.dismiss(t.id)
                                }}
                                className={`px-3 py-1 text-xs rounded-md text-white ${novoValor ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                              >
                                Confirmar
                              </button>
                            </div>
                          </div>
                        ), { duration: 10000 })
                      }}
                      className="h-4 w-4 text-rematei-blue focus:ring-rematei-blue border-gray-300 rounded"
                    />
                    <label htmlFor="is_socio" className="ml-2 block text-sm text-gray-900">
                      Cliente é sócio
                    </label>
                  </div>
                </div>

                {/* Restrição de Compra */}
                <div className="p-4 border border-gray-200 rounded-lg space-y-3">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="restrito_compra"
                      checked={formData.restrito_compra}
                      onChange={(e) => {
                        if (readOnly) return
                        const novoValor = e.target.checked
                        setFormData(prev => ({
                          ...prev,
                          restrito_compra: novoValor,
                          restrito_compra_motivo: novoValor ? prev.restrito_compra_motivo : ''
                        }))
                      }}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      disabled={readOnly}
                    />
                    <label htmlFor="restrito_compra" className="ml-2 block text-sm font-medium text-red-700">
                      ⚠️ Restrito para compra
                    </label>
                  </div>
                  {formData.restrito_compra && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Motivo da restrição *
                      </label>
                      <input
                        type="text"
                        value={formData.restrito_compra_motivo}
                        onChange={(e) => !readOnly && setFormData({...formData, restrito_compra_motivo: e.target.value})}
                        className="input-rematei border-red-300 focus:ring-red-500 focus:border-red-500"
                        placeholder="Ex: Inadimplente, cheque devolvido..."
                        required
                        readOnly={readOnly}
                      />
                    </div>
                  )}
                </div>

                {/* Pisteiro (Carteira) */}
                {organizadorAtivo?.produtos_premium?.includes('quem_vendeu_trouxe') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🎯 Pisteiro (Carteira)
                  </label>
                  <select
                    value={formData.pisteiro_id}
                    onChange={(e) => !readOnly && setFormData({...formData, pisteiro_id: e.target.value})}
                    className={`input-rematei ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    disabled={readOnly}
                  >
                    <option value="">Nenhum pisteiro vinculado</option>
                    {pisteiros.map(p => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Indica de qual pisteiro este cliente faz parte da carteira
                  </p>
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Observações
                  </label>
                  <textarea
                    value={formData.observacoes}
                    onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                    className="input-rematei"
                    rows="3"
                    placeholder="Observações sobre o cliente..."
                  />
                </div>
              </div>
            )}

            {/* Endereço */}
            {activeTab === 'endereco' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CEP
                    </label>
                    <input
                      type="text"
                      value={formData.cep}
                      onChange={(e) => handleCepChange(e.target.value)}
                      className="input-rematei"
                      placeholder="00000-000"
                      maxLength="9"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Endereço
                    </label>
                    <input
                      type="text"
                      value={formData.endereco}
                      onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                      className="input-rematei"
                      placeholder="Rua, Avenida..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Número
                    </label>
                    <input
                      type="text"
                      value={formData.numero}
                      onChange={(e) => setFormData({...formData, numero: e.target.value})}
                      className="input-rematei"
                      placeholder="123"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={formData.complemento}
                      onChange={(e) => setFormData({...formData, complemento: e.target.value})}
                      className="input-rematei"
                      placeholder="Apto, Sala, Bloco..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bairro
                    </label>
                    <input
                      type="text"
                      value={formData.bairro}
                      onChange={(e) => setFormData({...formData, bairro: e.target.value})}
                      className="input-rematei"
                      placeholder="Centro"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cidade
                    </label>
                    <CidadeAutocomplete
                      value={formData.cidade}
                      onChange={(cidade) => setFormData(prev => ({...prev, cidade}))}
                      onEstadoChange={(estado) => setFormData(prev => ({...prev, estado}))}
                      placeholder="Digite a cidade..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Estado
                    </label>
                    <input
                      type="text"
                      maxLength="2"
                      value={formData.estado}
                      onChange={(e) => setFormData({...formData, estado: e.target.value.toUpperCase()})}
                      className="input-rematei"
                      placeholder="UF"
                      readOnly
                    />
                  </div>
                </div>
              </div>
            )}



            {/* Comissões */}
            {activeTab === 'comissoes' && (
              <div className="space-y-6">
                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                  <Percent className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Comissão Individual</p>
                    <p className="text-xs text-blue-600 mt-0.5">
                      Quando preenchida, esta % substitui a comissão padrão do leilão para este cliente. Deixe em branco para usar o padrão.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comissão de Venda (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.comissao_venda_pct}
                      onChange={(e) => !readOnly && setFormData({...formData, comissao_venda_pct: e.target.value})}
                      className="input-rematei"
                      placeholder="Padrão do leilão"
                      readOnly={readOnly}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comissão de Compra (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.comissao_compra_pct}
                      onChange={(e) => !readOnly && setFormData({...formData, comissao_compra_pct: e.target.value})}
                      className="input-rematei"
                      placeholder="Padrão do leilão"
                      readOnly={readOnly}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comissão de Defesa (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.comissao_defesa_pct}
                      onChange={(e) => !readOnly && setFormData({...formData, comissao_defesa_pct: e.target.value})}
                      className="input-rematei"
                      placeholder="Padrão do leilão"
                      readOnly={readOnly}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Outras abas - placeholder */}
            {['analise'].includes(activeTab) && (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  {activeTab === 'analise' && <Eye className="w-16 h-16 mx-auto" />}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {activeTab === 'analise' && 'Análise 360°'}
                </h3>
                <p className="text-gray-500">
                  Funcionalidade em desenvolvimento
                </p>
              </div>
            )}

            {/* Footer */}
            {['dados', 'endereco', 'comissoes'].includes(activeTab) && !readOnly && (
              <div className="flex justify-end pt-6 border-t border-gray-200 mt-8">
                <button type="submit" className="btn-primary">
                  {isNew ? 'Criar Cliente' : 'Salvar Alterações'}
                </button>
              </div>
            )}
            </form>
          </div>
        )}
        
        {/* Abas fora do form para evitar submits acidentais */}
        {activeTab === 'fazendas' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
            <FazendasTab 
              cliente={cliente} 
              organizadorId={organizadorId} 
              onUpdate={loadCliente}
            />
          </div>
        )}

        {activeTab === 'bancarios' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
            <DadosBancariosTab 
              cliente={cliente} 
              organizadorId={organizadorId} 
              onUpdate={loadCliente}
            />
          </div>
        )}

        {activeTab === 'balanco' && (
          <div className="mt-6">
            <BalancoTab
              cliente={cliente}
              organizadorId={organizadorId}
            />
          </div>
        )}

        {activeTab === 'cadastro-publico' && (
          <div className="mt-6 space-y-4">
            {loadingSubmissoes ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : submissoesCadastro.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                Nenhum envio encontrado para este cliente.
              </div>
            ) : (
              submissoesCadastro.map((s) => (
                <SubmissaoCadastroPublicoCard key={s.id} submissao={s} />
              ))
            )}
          </div>
        )}
      </div>
    </>
  )
}
